import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Radio, Space, TimePicker, Typography } from 'antd';
import _ from 'lodash';
import moment from 'moment';

import { disabledMinutes, updateFilterItemInList } from '../../../../../../../helpers/utils/reports';
import { useActions } from '../../../../../hooks/useActions';
import { useTypedSelector } from '../../../../../hooks/useTypedSelector';

const FINAL_FORMAT = 'HH:mm';
const TIME_FORMATS = [moment.ISO_8601, 'HH:mm'];

/** Интерфейс данных фильтра */
interface FilterData {
    start: moment.Moment | string | null;
    end: moment.Moment | string | null;
    [key: string]: unknown;
}

/** Props для TimeRangePicker */
interface TimeRangePickerProps {
    isModal?: boolean;
    isLocalMode?: boolean;
    localFilters?: any[];
    setLocalFilters?: (filters: any[]) => void;
    disabled?: boolean;
    id: string;
    index?: number;
    modalVisibleHandler: (visible: boolean) => void;
    itemData?: FilterData;
    list?: boolean;
    [key: string]: unknown;
}

/** Props для внутреннего компонента TimePicker */
interface TPProps {
    value: moment.Moment | string | null;
    type: 'start' | 'end';
    disabled: boolean;
    onChange: (type: 'start' | 'end', val: moment.Moment) => void;
    disabledHours?: () => number[];
    getDisabledMinutes?: (hour: number) => number[];
}

const TP: React.FC<TPProps> = ({ value, type, disabled, onChange, disabledHours, getDisabledMinutes }) => {
    const _val = value ? moment(value, TIME_FORMATS) : null;
    const [ open, setOpen ] = useState(false);
    const lastMinuteRef = useRef<number | null>(null);

    const handleSelect = useCallback((val: moment.Moment) => {
        onChange(type, val);
        const newMinute = val.minute();
        if (lastMinuteRef.current !== null && lastMinuteRef.current !== newMinute) {
            setOpen(false);
        }
        lastMinuteRef.current = newMinute;
    }, [ type, onChange ]);

    const handleOpenChange = useCallback((newOpen: boolean) => {
        setOpen(newOpen);
        if (newOpen) {
            lastMinuteRef.current = _val ? _val.minute() : null;
        }
    }, [ _val ]);

    return (
        <TimePicker
            disabled={disabled}
            placeholder={type === 'start' ? 'с' : 'по'}
            format="HH:mm"
            disabledHours={disabledHours}
            disabledMinutes={getDisabledMinutes || disabledMinutes}
            hideDisabledOptions
            showNow={false}
            value={_val}
            open={open}
            onOpenChange={handleOpenChange}
            onSelect={handleSelect}
        />
    );
};

const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
    isModal = false,
    isLocalMode = false,
    localFilters,
    setLocalFilters,
    disabled = false,
    id,
    index,
    modalVisibleHandler,
    itemData,
    list,
    ...props
}) => {
    const { filters: globalFilters } = useTypedSelector((state) => 
        isModal ? state.filter.dashboardModal : state.filter
    );
    const filters = isLocalMode ? localFilters || [] : globalFilters;
    const { setFilters, setFilter, setMetaFilterUpdate } = useActions();

    const [filterMode, setFilterMode] = useState(1);

    // Приводим id к строке для безопасной работы с методами строк
    const safeId = String(id ?? '');

    // Вычисляем ID с учётом режима include/exclude
    const filterId = useMemo(() => {
        if (filterMode === 2) {
            return safeId.includes('not_') ? safeId : `not_${safeId}`;
        }
        return safeId.includes('not_') ? safeId.split('not_')[1] : safeId;
    }, [safeId, filterMode]);

    // Инициализация режима из id
    useEffect(() => {
        setFilterMode(safeId.includes('not_') ? 2 : 1);
    }, [safeId]);

    // Определяем режим редактирования
    const isEditing = useMemo(() => {
        return Boolean(list && itemData);
    }, [list, itemData]);

    // Мемоизированные начальные данные фильтра
    const initialFilter = useMemo((): FilterData => {
        if (itemData && list) {
            return itemData;
        }
        return { start: null, end: null, ...props };
    }, [itemData, list, props]);

    const [currentData, setCurrentData] = useState<FilterData>(initialFilter);

    // Синхронизация currentData при изменении itemData
    useEffect(() => {
        if (itemData && list) {
            setCurrentData(itemData);
        }
    }, [itemData, list]);

    const onApply = useCallback(() => {
        const _currentData = {
            ...currentData,
            id: filterId,
            start: currentData.start
                ? moment(currentData.start, TIME_FORMATS).format(FINAL_FORMAT)
                : moment('00:00', TIME_FORMATS).format(FINAL_FORMAT),
            end: currentData.end
                ? moment(currentData.end, TIME_FORMATS).format(FINAL_FORMAT)
                : moment('23:59', TIME_FORMATS).format(FINAL_FORMAT)
        };

        if (isLocalMode && setLocalFilters) {
            // Локальный режим
            if (isEditing && index !== undefined) {
                let _filters = _.cloneDeep(filters);
                _filters = updateFilterItemInList(_filters, index, _currentData);
                setLocalFilters(_filters);
            } else {
                setLocalFilters([...filters, _currentData]);
            }
        } else {
            // Глобальный режим
            if (isEditing && index !== undefined) {
                let _filters = _.cloneDeep(filters);
                _filters = updateFilterItemInList(_filters, index, _currentData);
                setFilters(isModal ? { isModal, data: _filters } : _filters);
            } else {
                setFilter({ ..._currentData, isModal });
            }
            setMetaFilterUpdate({ value: true });
        }

        modalVisibleHandler(false);
    }, [currentData, filterId, isEditing, index, filters, isLocalMode, setLocalFilters, isModal, setFilters, setFilter, setMetaFilterUpdate, modalVisibleHandler]);

    const handleTimeChange = useCallback((type: 'start' | 'end', val: moment.Moment) => {
        setCurrentData((prevState) => ({
            ...prevState,
            [type]: val
        }));
    }, []);

    // --- Ограничения: ОТ всегда раньше ДО ---
    const baseDisabled = useMemo(() => disabledMinutes(), []);

    const startDisabledHours = useCallback(() => {
        if (!currentData.end) return [];
        const endM = moment(currentData.end, TIME_FORMATS);
        if (!endM.isValid()) return [];
        const eH = endM.hour();
        return Array.from({ length: 24 }, (_, i) => i).filter(h => h > eH);
    }, [currentData.end]);

    const startGetDisabledMinutes = useCallback((hour: number) => {
        if (!currentData.end) return baseDisabled;
        const endM = moment(currentData.end, TIME_FORMATS);
        if (!endM.isValid()) return baseDisabled;
        const eH = endM.hour();
        const eMin = endM.minute();
        if (hour < eH) return baseDisabled;
        if (hour === eH) {
            const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m > eMin);
            return [ ...new Set([ ...baseDisabled, ...range ]) ];
        }
        return Array.from({ length: 60 }, (_, i) => i);
    }, [currentData.end, baseDisabled]);

    const endDisabledHours = useCallback(() => {
        if (!currentData.start) return [];
        const startM = moment(currentData.start, TIME_FORMATS);
        if (!startM.isValid()) return [];
        const sH = startM.hour();
        return Array.from({ length: 24 }, (_, i) => i).filter(h => h < sH);
    }, [currentData.start]);

    const endGetDisabledMinutes = useCallback((hour: number) => {
        if (!currentData.start) return baseDisabled;
        const startM = moment(currentData.start, TIME_FORMATS);
        if (!startM.isValid()) return baseDisabled;
        const sH = startM.hour();
        const sMin = startM.minute();
        if (hour > sH) return baseDisabled;
        if (hour === sH) {
            const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m < sMin);
            return [ ...new Set([ ...baseDisabled, ...range ]) ];
        }
        return Array.from({ length: 60 }, (_, i) => i);
    }, [currentData.start, baseDisabled]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !disabled) {
            e.preventDefault();
            onApply();
        }
    }, [disabled, onApply]);

    return (
        <div onKeyDown={handleKeyDown}>
            <Space size="small" direction="vertical">
                <div className="picker-radio-group">
                    <Radio.Group disabled={disabled} onChange={(e) => setFilterMode(e.target.value)} value={filterMode}>
                        <Radio value={1}>Включить</Radio>
                        <Radio value={2}>Исключить</Radio>
                    </Radio.Group>
                </div>
                <div>
                    <TP value={currentData.start} type="start" disabled={disabled} onChange={handleTimeChange} disabledHours={startDisabledHours} getDisabledMinutes={startGetDisabledMinutes} />
                    <Typography.Text type="secondary">—</Typography.Text>
                    <TP value={currentData.end} type="end" disabled={disabled} onChange={handleTimeChange} disabledHours={endDisabledHours} getDisabledMinutes={endGetDisabledMinutes} />
                </div>
            </Space>
            <br />
            <Button disabled={disabled} type="primary" className="picker-apply-btn-time" onClick={onApply}>
                Применить
            </Button>
        </div>
    );
};

export default TimeRangePicker;
